import { forwardRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Icon, type IconName } from '../../icons/Icon';
import { Button } from '../Button';
import { DiagnosticDetails } from './DiagnosticDetails';
import { useDsT } from '../../services/i18n';

interface ReferenceLineProps {
  id: string;
  label: string;
  copyLabel: string;
  copiedLabel: string;
  className?: string;
}

/** "Reference: <mono id>" with a copy button — the one line an operator reads to support. */
function ReferenceLine({ id, label, copyLabel, copiedLabel, className }: ReferenceLineProps) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable: the id stays selectable */
    }
  };
  return (
    <div data-error-reference="" className={cn('flex min-w-0 items-center gap-2 type-caption text-fg-muted', className)}>
      <span>{label}</span>
      <span className="truncate type-mono text-fg select-all">{id}</span>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={copied ? copiedLabel : copyLabel}
        title={copied ? copiedLabel : copyLabel}
        className="inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-fg-subtle transition-colors motion-fast hover:bg-hover hover:text-fg focus-ring"
      >
        <Icon name={copied ? 'check' : 'copy'} size={12} />
      </button>
    </div>
  );
}

export interface ErrorStateFieldError {
  /** The offending field, as the server named it. Omitted for a message about the request as a whole. */
  field?: string;
  message: ReactNode;
}

export interface ErrorStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** What could not happen, in the operator's terms ("Outbound orders couldn't be loaded"). */
  title: ReactNode;
  /** What it means and what to do. Never a stack trace, schema path or exception name. */
  description?: ReactNode;
  /** @deprecated use `description`. */
  detail?: ReactNode;
  /** Field-level messages from a validation failure (forms only). */
  errors?: ErrorStateFieldError[];
  /** Stable failure code, shown only under "Technical details". */
  code?: string;
  /** Correlation id, shown only under "Technical details" with a copy action. */
  correlationId?: string;
  /** HTTP status, shown only under "Technical details". */
  status?: number;
  /** Support reference shown in the open as "Reference: <id>" with a copy button (unlike `correlationId`, which stays folded). */
  referenceId?: string;
  referenceLabel?: string;
  copyReferenceLabel?: string;
  onRetry?: () => void;
  /** Retry in flight: the button shows progress and the surface stays put. */
  retrying?: boolean;
  retryLabel?: ReactNode;
  /**
   * `inline` sits inside a panel or grid; `page` centres itself when a whole screen has nothing else to show;
   * `compact` is one line for a dashboard widget or a rail section that degraded while the rest of the page works.
   */
  variant?: 'inline' | 'page' | 'compact';
  /** `neutral` (default) for things that could not load; `critical` for a failed action the user must notice. */
  tone?: 'neutral' | 'critical';
  /** Extra action, e.g. "View other work". */
  action?: ReactNode;
  icon?: IconName;
  /**
   * Heading level of `title` in the `inline` and `page` variants. An error state replaces the content of the
   * region it sits in, so its title is that region's heading — a screen that failed to load must still be
   * navigable by heading. `compact` is one line in a rail and stays unheaded.
   */
  headingLevel?: 1 | 2 | 3 | 4;
  technicalLabel?: string;
  diagnosticLabel?: string;
  copyLabel?: string;
  copiedLabel?: string;
  /** @deprecated kept for older call sites; ignored. */
  correlationLabel?: string;
}

/**
 * The failure state of an async surface.
 *
 * Calm by default: a failed read is not the operator's mistake, so it is rendered as a quiet, contained notice in
 * the space the data would have used — the rest of the screen keeps working. The body says what could not happen
 * and what to do; the correlation id, code and status live behind "Technical details" with a copy action for
 * support. Validation messages are listed only when the failure is about the user's input.
 */
export const ErrorState = forwardRef<HTMLDivElement, ErrorStateProps>(function ErrorState(
  {
    title,
    description,
    detail,
    errors,
    code,
    correlationId,
    status,
    referenceId,
    referenceLabel: referenceLabelProp,
    copyReferenceLabel: copyReferenceLabelProp,
    onRetry,
    retrying = false,
    retryLabel: retryLabelProp,
    variant = 'inline',
    tone = 'neutral',
    action,
    icon,
    headingLevel = 2,
    technicalLabel: technicalLabelProp,
    diagnosticLabel: diagnosticLabelProp,
    copyLabel: copyLabelProp,
    copiedLabel: copiedLabelProp,
    correlationLabel: _correlationLabel,
    className,
    ...rest
  },
  ref,
) {
  const body = description ?? detail;
  const t = useDsT();
  const referenceLabel = referenceLabelProp ?? t('ds.errorState.reference', 'Reference:');
  const copyReferenceLabel = copyReferenceLabelProp ?? t('ds.errorState.copyReference', 'Copy reference');
  const retryLabel = retryLabelProp === undefined ? t('ds.errorState.retry', 'Try again') : retryLabelProp;
  const technicalLabel = technicalLabelProp ?? t('ds.errorState.technical', 'Technical details');
  const diagnosticLabel = diagnosticLabelProp ?? t('ds.errorState.diagnosticId', 'Diagnostic ID');
  const copyLabel = copyLabelProp ?? t('ds.errorState.copyDiagnosticId', 'Copy diagnostic ID');
  const copiedLabel = copiedLabelProp ?? t('ds.common.copied', 'Copied');
  const fieldErrors = (errors ?? []).filter((e) => e.message);
  const hasTechnical = Boolean(code || correlationId || status);
  const critical = tone === 'critical';
  const glyph: IconName = icon ?? (critical ? 'alert-circle' : 'cloud-off');

  if (variant === 'compact') {
    return (
      <div
        ref={ref}
        role="alert"
        className={cn('zo-error-state zo-error-state--compact flex min-h-9 items-center gap-2 text-sm text-fg-muted', className)}
        {...rest}
      >
        <Icon name={glyph} size={14} className={cn('shrink-0', critical ? 'text-danger' : 'text-fg-subtle')} />
        <span className="min-w-0 flex-1 truncate" title={typeof body === 'string' ? body : undefined}>
          <span className="font-medium text-fg">{title}</span>
          {body ? <span className="sr-only"> {body}</span> : null}
        </span>
        {referenceId ? <ReferenceLine id={referenceId} label={referenceLabel} copyLabel={copyReferenceLabel} copiedLabel={copiedLabel} className="shrink-0" /> : null}
        {onRetry ? (
          <Button variant="ghost" size="sm" loading={retrying} onClick={onRetry} iconStart={<Icon name="refresh" size={13} />}>
            {retryLabel}
          </Button>
        ) : null}
      </div>
    );
  }

  const Heading = `h${headingLevel}` as 'h2';
  return (
    <div
      ref={ref}
      role="alert"
      className={cn(
        'zo-error-state flex gap-3 rounded-md p-4 text-sm',
        critical ? 'bg-danger-bg shadow-[inset_0_0_0_1px_var(--zo-danger-border)]' : 'bg-surface-sunken',
        variant === 'page' && 'mx-auto my-12 max-w-md flex-col items-center p-8 text-center',
        className,
      )}
      {...rest}
    >
      <span
        className={cn(
          'inline-flex size-8 shrink-0 items-center justify-center rounded-lg',
          critical ? 'bg-danger-subtle text-danger-fg' : 'bg-neutral-subtle text-fg-muted',
          variant === 'page' && 'size-10',
        )}
        aria-hidden
      >
        <Icon name={glyph} size={variant === 'page' ? 18 : 16} />
      </span>
      <div className={cn('flex min-w-0 flex-1 flex-col gap-1', variant === 'page' && 'items-center')}>
        <Heading className="m-0 type-body-strong text-fg">{title}</Heading>
        {body ? <p className="m-0 max-w-prose text-fg-muted">{body}</p> : null}
        {fieldErrors.length > 0 ? (
          <ul className="m-0 mt-1 list-disc ps-5 text-start text-fg-muted">
            {fieldErrors.map((e, i) => (
              <li key={`${e.field ?? ''}-${i}`}>
                {e.field ? <span className="font-medium text-fg">{e.field}: </span> : null}
                {e.message}
              </li>
            ))}
          </ul>
        ) : null}
        {onRetry || action ? (
          <div className={cn('mt-2 flex flex-wrap items-center gap-2', variant === 'page' && 'justify-center')}>
            {onRetry ? (
              <Button variant="secondary" size="sm" loading={retrying} iconStart={<Icon name="refresh" size={13} />} onClick={onRetry}>
                {retryLabel}
              </Button>
            ) : null}
            {action}
          </div>
        ) : null}
        {referenceId ? (
          <ReferenceLine
            id={referenceId}
            label={referenceLabel}
            copyLabel={copyReferenceLabel}
            copiedLabel={copiedLabel}
            className={cn('mt-2', variant === 'page' && 'justify-center')}
          />
        ) : null}
        {hasTechnical ? (
          <div className={cn('mt-2', variant === 'page' && 'flex flex-col items-center')}>
            <DiagnosticDetails
              code={code}
              correlationId={correlationId}
              status={status}
              label={technicalLabel}
              diagnosticLabel={diagnosticLabel}
              copyLabel={copyLabel}
              copiedLabel={copiedLabel}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
});
